import type { Entry } from "contentful";
import { observer } from "mobx-react-lite";
import type { EmbeddedEntryContextType } from "../../common/types/cmsTypes";
import type { TypeAutomaticGeneratedProductListSkeleton } from "../../common/types/contentful";
import { LIST_TYPE } from "../../common/types/trackingTypes";
import { getModuleVisibility } from "../../common/utils/segmentUtils";
import authStore from "../../stores/auth/authStore";
import segmentStore from "../../stores/cms/segmentStore";
import favoriteStore from "../../stores/favoriteStore";
import productStore from "../../stores/product/productStore";
import { shouldShowExpiryNotice } from "../../stores/product/productUtils";
import ContractualProductList from "../ContractualProductList";
import ProductList from "../common/product/ProductList";
import ErrorAlertWithOptionalTracking from "../ErrorAlertWithOptionalTracking";
import Recommendations from "../Recommendations";

type CmsAutomaticProductListProps = {
   content: Entry<TypeAutomaticGeneratedProductListSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", "nb_NO">;
   context: EmbeddedEntryContextType;
};

const CmsAutomaticProductList = ({ content, context }: CmsAutomaticProductListProps) => {
   const title = content.fields.title;
   const type = content.fields.type;
   const productCount = content.fields.productCount;
   const numberOfProducts: number =
      productCount === "Ubegrenset"
         ? Number.MAX_SAFE_INTEGER
         : productCount
           ? Number.parseInt(productCount)
           : content.fields.numberOfProducts || Number.MAX_SAFE_INTEGER;

   if (!authStore.isLoggedIn()) {
      return null;
   }
   const isVisible = getModuleVisibility(content, segmentStore.currentCustomerProperties);
   if (!isVisible) {
      return null;
   }

   switch (type) {
      case "User Favorites": {
         const skus = productStore.resolveSkus(favoriteStore.allSkus, "inAssortment").map((product) => product.sku);
         return (
            <ProductList
               skus={skus.slice(0, numberOfProducts)}
               buttonUrl="/customer/favorites"
               listName="Mine Favoritter (CMS Block)"
               listType={LIST_TYPE.RECOMMENDATIONS}
               heading={title}
               context={context}
            />
         );
      }

      case "Contractual Recommendations - Should Buy":
         return <ContractualProductList heading={title} count={numberOfProducts} type="shouldBuy" />;

      case "Contractual Recommendations - Full List":
         return <ContractualProductList heading={title} count={numberOfProducts} type="fullList" />;

      case "Recommendations":
         return <Recommendations heading={null} count={numberOfProducts} />;

      case "Expired": {
         return (
            <ProductList
               skus={productStore.expiredSKUs}
               heading={title}
               listName="Expired products"
               listType={LIST_TYPE.EXPIRED}
               context="expired-products"
            />
         );
      }

      case "Last Chance": {
         const lastChanceProducts = productStore.products
            .filter((product) => shouldShowExpiryNotice(product))
            .map((product) => product.sku);

         return (
            <ProductList
               skus={lastChanceProducts}
               heading={title}
               listName="Last chance"
               listType={LIST_TYPE.LAST_CHANCE}
               context="last-chance-products"
            />
         );
      }

      default:
         return (
            <ErrorAlertWithOptionalTracking
               showCloseButton={false}
               gaErrorTracking={{ context: "cms_content", text: "products_error" }}
            >
               Det opppsto en uventet feil under visning av innhold {content.sys.id}
            </ErrorAlertWithOptionalTracking>
         );
   }
};

export default observer(CmsAutomaticProductList);
